// Global Types
import type {
  $Translate,
} from 'common.language';
import type{
  $Model,
  $RegattaEntryResource,
} from 'common.data';

// Global Components
import * as React from 'react';
import {
  Button,
  Col,
  Row,
} from 'reactstrap';

// Helpers
import _ from 'lodash';
import moment from 'moment';
import {
  cleanFormValues,
} from '@vroskus/library-helpers/dist/browser';
import {
  getRegattaEntryParams,
  getRegattaEntrySkipperName,
  shortenText,
} from 'common.helpers/dist/browser';
import {
  defaultUpsertItem,
} from '../../../helpers';

// Components
import InputResult from '../../form/InputResult';

// Services
import Services from '../../../services';

// Types
import type {
  $TileResult,
} from '../../../types';

enum Action {
  click = 'click',
  longpress = 'longpress',
}

type $Props = $Translate & {
  mode: 'position' | 'roundings' | 'time';
  regatta: $Model<'Regatta'>;
  result: $TileResult;
};

type $State = {
  action: Action;
  resultCopy: $TileResult | null;
};

class ResultTileView extends React.Component<$Props, $State> {
  timerRef: React.MutableRefObject<null | ReturnType<typeof setTimeout>>;

  isLongPress: React.MutableRefObject<boolean | null>;

  constructor(props: $Props) {
    super(props);

    this.timerRef = React.createRef<null | ReturnType<typeof setTimeout>>();
    this.isLongPress = React.createRef<boolean | null>();

    this.state = {
      action: Action.click,
      resultCopy: null,
    };
  }

  handleOnClick(): void {
    const {
      result,
    } = this.props;

    if (this.isLongPress.current) {
      this.setState({
        resultCopy: result,
      });
    } else if (this.isResultNotEmpty() === false) {
      this.saveResult();
    }

    this.setState({
      action: Action.click,
    });
  }

  handleOnMouseDown(): void {
    this.startPressTimer();
  }

  handleOnMouseUp(): void {
    if (this.timerRef.current !== null) {
      clearTimeout(this.timerRef.current);
    }
  }

  handleOnTouchStart(): void {
    this.startPressTimer();
  }

  handleOnTouchEnd(): void {
    const {
      action,
    } = this.state;

    if (action === Action.longpress) {
      return;
    }

    if (this.timerRef.current !== null) {
      clearTimeout(this.timerRef.current);
    }
  }

  startPressTimer(): void {
    this.isLongPress.current = false;

    this.timerRef.current = setTimeout(
      () => {
        this.isLongPress.current = true;

        this.setState({
          action: Action.longpress,
        });
      },
      2000,
    );
  }

  prepareResult(result: $TileResult) {
    const {
      mode,
    } = this.props;

    const updatedResult = {
      ...result,
      InputDate: new Date(),
    };

    if (mode === 'roundings') {
      updatedResult.Second = Number(updatedResult.Second) + 1;

      return updatedResult;
    }

    if (mode === 'time') {
      const now: moment.Moment = moment();

      updatedResult.Month = Number(now.format('MM'));
      updatedResult.Day = Number(now.format('DD'));
      updatedResult.Hour = Number(now.format('HH'));
      updatedResult.Minute = Number(now.format('mm'));
      updatedResult.Second = Number(now.format('ss'));

      return updatedResult;
    }

    const positions: Array<number> = result
      .RegattaResultTableRace.RegattaResultTableRaceResults.map(({
        Position,
      }) => {
        if (Position && /^-?\d+$/.test(Position)) {
          return Number(Position);
        }

        return 0;
      });

    const highestPosition: number = _.max(positions) || 0;

    updatedResult.Position = String(highestPosition + 1);

    return updatedResult;
  }

  async saveResult(): Promise<void> {
    const {
      result,
    } = this.props;

    await defaultUpsertItem({
      action: async () => {
        const updatedResult = this.prepareResult(result);

        await Services.Api.updateRegattaResultTableRaceResult(updatedResult);

        this.setState({
          resultCopy: null,
        });
      },
      quiet: true,
    });
  }

  isResultNotEmpty(): boolean {
    const {
      result,
    } = this.props;

    return result.Position !== null || result.ET !== 0;
  }

  renderTitle() {
    const {
      regatta,
      result,
    } = this.props;

    const regattaEntry: $RegattaEntryResource | undefined = _.get(
      result,
      'RegattaResultTableEntry.RegattaEntry',
    );

    const {
      bowNoRegNoString,
    } = getRegattaEntryParams({
      bowNoEnabled: regatta.BowNoEnabled,
      countryEnabled: regatta.CountryEnabled,
      regattaEntry,
    });

    return (
      <>
        {regattaEntry && (
          <>
            <h4 className={'h4 mb-1'}>
              {bowNoRegNoString}
            </h4>
            <div className={'text-success h5 mb-0'}>
              {shortenText(
                regattaEntry.Name || getRegattaEntrySkipperName({
                  regattaEntry,
                }),
                19,
              )}
            </div>
          </>
        )}
        <div className={'text-muted mb-1'}>
          {result.RegattaResultTable.Name}
        </div>
      </>
    );
  }

  renderData() {
    const {
      mode,
      result,
    } = this.props;

    if (mode === 'roundings') {
      return result.Second;
    }

    if (mode === 'time') {
      return (
        <>
          <span>{result.Hour}</span>
          <span className={'text-muted'}>{':'}</span>
          <span>{result.Minute}</span>
          <span className={'text-muted'}>{':'}</span>
          <span>{result.Second}</span>
        </>
      );
    }

    const Position: string = _.get(
      result,
      'Position',
    ) || '-';

    return Position;
  }

  renderResultEdit() {
    const {
      result,
      t,
    } = this.props;
    const {
      resultCopy,
    } = this.state;

    return (resultCopy !== null ? (
      <>
        <InputResult
          item={resultCopy}
          onChange={(values) => {
            const params = cleanFormValues<$TileResult>({
              ...result,
              ...values,
            });

            this.setState({
              resultCopy: params,
            });
          }}
          raceType={resultCopy.RegattaResultTableRace.Type}
        />
        <Row className={'no-gutters'}>
          <Col>
            <Button
              className={'w-100 mt-1'}
              color={'success'}
              id={`result-tile-confirm-button-${result.id}`}
              onClick={() => defaultUpsertItem({
                action: async () => {
                  const params: $TileResult = resultCopy;

                  await Services.Api.updateRegattaResultTableRaceResult(params);

                  this.setState({
                    resultCopy: null,
                  });
                },
              })}
              size={'sm'}
            >
              {t('Save')}
            </Button>
          </Col>
          <Col>
            <Button
              className={'w-100 mt-1'}
              color={'secondary'}
              id={`result-tile-clear-button-${result.id}`}
              onClick={() => this.setState({
                resultCopy: {
                  ...result,
                  Day: null,
                  Hour: null,
                  Minute: null,
                  Month: null,
                  Position: null,
                  Second: null,
                },
              })}
              size={'sm'}
            >
              {t('Clear')}
            </Button>
          </Col>
          <Col>
            <Button
              className={'w-100 mt-1'}
              color={'secondary'}
              id={`result-tile-close-button-${result.id}`}
              onClick={() => this.setState({
                resultCopy: null,
              })}
              size={'sm'}
            >
              {t('Close')}
            </Button>
          </Col>
        </Row>
      </>
    ) : null);
  }

  render() {
    const {
      result,
    } = this.props;

    const isResultNotEmpty: boolean = this.isResultNotEmpty();

    return (
      <Button
        className={`w-100 ${isResultNotEmpty === true ? 'bg-lightgrey' : ''}`}
        color={'secondary'}
        id={`result-tile-${result.id}`}
        onClick={() => this.handleOnClick()}
        onMouseDown={() => this.handleOnMouseDown()}
        onMouseUp={() => this.handleOnMouseUp()}
        onTouchEnd={() => this.handleOnTouchEnd()}
        onTouchStart={() => this.handleOnTouchStart()}
        size={'sm'}
        style={{
          minHeight: 92,
        }}
      >
        {this.renderTitle()}
        <div className={'h5 mb-0'}>
          {this.renderData()}
        </div>
        {this.renderResultEdit()}
      </Button>
    );
  }
}

export default ResultTileView;
